import React from "react";

/**
 * Career Hero (React + Tailwind)
 * Inspired by your screenshot:
 * - Big centered heading + subtext
 * - Search bar with CTA button
 * - Hero people image in center
 * - Floating stat badges around the image
 * - Greenish accent + clean white background
 *
 * Replace:
 * - HeroImg import with your own image
 * - Stats text / numbers
 */

import HeroImg from "../../Images/career.webp"; // <-- replace with your image

const Hero = () => {
  return (
    <section className="relative overflow-hidden bg-white pt-12">
      {/* soft grid / background */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 opacity-[0.55] [background-image:linear-gradient(to_right,rgba(2,6,23,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,0.06)_1px,transparent_1px)] [background-size:56px_56px]" />
        <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[#d8ddda73] blur-3xl" />
        <div className="absolute -bottom-28 right-[-80px] h-[520px] w-[520px] rounded-full bg-green-500/10 blur-3xl" />
      </div>

      <div className="relative mx-auto w-[90%] max-w-[1280px] pt-14 md:pt-20 pb-10 md:pb-14">
        {/* Heading */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-[#cac6c6]  px-3 py-1 text-xs font-semibold text-[#8ec700]">
            Careers • Join our team
            <span className="h-1 w-1 rounded-full bg-emerald-600" />
            Remote & On-site
          </p>

          <h1 className="mt-4  h1-mb h1-md h1-lg h1-2xl">
           Karriere bei UNEVA 
          </h1>

          <p className="mt-4 text-pretty text-base leading-relaxed text-slate-600 sm:text-lg">
            Wir suchen nach engagierten und talentierten Menschen, die gemeinsam mit uns die Zukunft gestalten wollen. Bei UNEVA legen wir Wert auf Teamgeist, Innovation und individuelle Weiterentwicklung. Entdecke, warum eine Karriere bei uns mehr als nur ein Job ist!
          </p>
        </div>

        {/* Hero Image + floating stats */}
        <div className="relative mx-auto mt-10 flex max-w-5xl items-end justify-center">
          {/* Image container */}
          <div className="relative">
            <div className="absolute inset-x-6 -bottom-5 h-14  blur-2xl" />

            <img
              src={HeroImg}
              alt="Team"
              className="relative z-10    object-cover "
            />

            {/* Badges */}
            <StatBadge
              className="left-[114px] top-[15%] "
              tone="emerald"
              icon={<UsersIcon />}
              title="30+ Companies"
              sub="Trusted employers"
            />

            <StatBadge
              className="right-[112px] top-[12%] "
              tone="amber"
              icon={<BoltIcon />}
              title="2× Faster"
              sub="Quick discovery"
            />

            <StatBadge
              className="left-[0px] top-[40%]"
              tone="sky"
              icon={<ChartIcon />}
              title="350+ Visits"
              sub="Monthly job seekers"
            />

            <StatBadge
              className="right-[6px] top-[40%] "
              tone="rose"
              icon={<BriefcaseIcon />}
              title="25+ Jobs"
              sub="Curated listings"
            />
          </div>
        </div>

        
      </div>
    </section>
  );
};

export default Hero;

/* ---------------- Small Components ---------------- */

function StatBadge({ className = "", tone = "emerald", icon, title, sub }) {
  const toneMap = {
    emerald: " border-[#cac6c6] text-[#8ec700]",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    sky: "bg-sky-50 border-sky-200 text-sky-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
  };

  return (
    <div
      className={[
        "absolute z-20 hidden sm:flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-sm backdrop-blur-sm",
        toneMap[tone],
        className,
      ].join(" ")}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/70 ring-1 ring-black/5">
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-600">{sub}</div>
      </div>
    </div>
  );
}

/* ---------------- Icons ---------------- */

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-slate-400">
      <path
        d="M10.5 18a7.5 7.5 0 1 1 7.5-7.5A7.5 7.5 0 0 1 10.5 18Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M16.2 16.2 21 21"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-[#8ec700]">
      <path
        d="M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M6 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M2 20c0-3 2.5-5 5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M10 20c0-4 3.1-6 6-6s6 2 6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-amber-700">
      <path
        d="M13 2 3 14h8l-1 8 11-14h-8l0-6Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-sky-700">
      <path
        d="M4 19V5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M4 19h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M8 16V10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M12 16V7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M16 16v-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BriefcaseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-rose-700">
      <path
        d="M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M4 8h16v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M4 12h16"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}
